import { cloneElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import Switch from 'react-switch';
import { NavigationService } from '../services/navigation.service';

import { GlobalService } from '../services/global.service';
import { UserService } from '../services/user.service';
import { ToastService } from 'services/toast.service';

import { ROUTES } from '../constants/navigation.constants';

import { Ability, APPS_NAME, Client, getAppsFromMask, RootState, Sector, Site, Skill, PostUser, LanguageService, CommonGlobalService } from '@umanforge/common';
import { ModifyUser } from 'interfaces/api_user.interfaces';
import { APPS_INFOS } from '../constants/app.constants';

import { HomeTopBar } from '../components/menu/home_top_bar.component';
import { Breadcrumb } from '../components/menu/breadcrumb.component';
import { AddUserFileds } from '../components/users/add_user_fields.component';
import { AddUserLinks } from '../components/users/add_user_links.component';
import { Button } from '../components/inputs/button.component';
import { Loading } from '../components/loading.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';

import { UsersIcon } from '../components/icons/users_icon.component';
import { UserIcon } from '../components/icons/user_icon.component';
import { SaveIcon } from '../components/icons/save_icon.component';

const toastService: ToastService = new ToastService( 'Create User' );

export function CreateUserView() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const dispatch = useDispatch();

  const TIMEZONES = moment.tz.names().map( tz => { return { value: tz, label: tz }; } );
  const defaultTimezone = me ? TIMEZONES.find( tz => { return tz.value === me!.timezone; } ) : undefined;
  const defaultLanguage = me ? LanguageService.getLanguages().find( lang => { return lang.value === me!.language; } ) : undefined;
  const defaultPostUser: PostUser = {
    email: '',
    firstname: '',
    lastname: '',
    language: defaultLanguage?.value ?? '',
    timezone: defaultTimezone?.label ?? '',
    clients: [],
    sites: [],
    sectors: [],
    abilities: [],
    skills: [],
    readAccessSectors: false,
    appAccess: 15
  };

  /*----------------------------- useState ------------------------------*/
  const [ postUser, setPostUser ] = useState<PostUser>( defaultPostUser );
  const [ readAccessSectors, setReadAccessSectors ] = useState<boolean>( defaultPostUser.readAccessSectors ?? false );
  const [ appAccessMask, setAppAccessMask ] = useState<number>( defaultPostUser.appAccess ?? 0 );

  const [ selectedClients, setSelectedClients ] = useState<Client[]>( [] );
  const [ selectedSites, setSelectedSites ] = useState<Site[]>( [] );
  const [ selectedSectors, setSelectedSectors ] = useState<Sector[]>( [] );

  const [ rerenderLinks, setRerenderLinks ] = useState<number>( 0 );
  const [ canPost, setCanPost ] = useState<boolean>( false );
  const [ hasChange, setHasChange ] = useState<boolean>( false );

  const [ isLoading, setIsLoading ] = useState<boolean>( false );

  /*---------------------------- useEffect ------------------------------*/
  useEffect( () => {
    resetForm();
  }, [] );

  useEffect( () => {
    if ( me ) {
      if ( !CommonGlobalService.isClientAdmin( me ) && !CommonGlobalService.isSiteAdmin( me ) && !CommonGlobalService.isSectorAdmin( me ) ) {
        NavigationService.toUsers( navigate, false );
      }
    }
  }, [ me ] );

  useEffect( () => {
    checkCanPostUser();
    checkHasChangeUser();
  }, [ postUser ] );

  const resetForm = () => {
    if ( me ) {
      const user = structuredClone( defaultPostUser );
      const connectedUserClients = CommonGlobalService.getUserClients( me );
      if ( connectedUserClients.length === 1 && !me.isSuperAdmin ) {
        user.clients = [ { id: connectedUserClients[ 0 ].id, isAdmin: false } ];
      }
      const connectedUserSites = CommonGlobalService.getUserSites( me );
      if ( connectedUserSites.length === 1 && !me.isSuperAdmin ) {
        user.sites = [ { id: connectedUserSites[ 0 ].id, isAdmin: false } ];
      }
      const connectedUserSectors = CommonGlobalService.getUserSectors( me );
      if ( connectedUserSectors.length === 1 && !me.isSuperAdmin ) {
        user.sectors = [ { id: connectedUserSectors[ 0 ].id, isAdmin: false } ];
      }
      user.readAccessSectors = defaultPostUser.readAccessSectors ?? false;
      user.appAccess = defaultPostUser.appAccess ?? 0;
      setPostUser( user );
      setReadAccessSectors( user.readAccessSectors ?? false );
      setAppAccessMask( user.appAccess ?? 0 );
      setRerenderLinks( rerenderLinks + 1 );
    }
  };

  const checkCanPostUser = () => {
    const emailChange = postUser.email != null && postUser.email != '' && GlobalService.validateEmail( postUser.email );
    const firstnameChange = postUser.firstname != null && postUser.firstname != '';
    const lastnameChange = postUser.lastname != null && postUser.lastname != '';
    const languageChange = postUser.language != null && postUser.language != '';
    const timezoneChange = postUser.timezone != null && postUser.timezone != '';
    const clientsChange = postUser.clients != null && postUser.clients.length > 0;
    const sitesChange = postUser.sites != null && postUser.sites.length > 0;
    const sectorsChange = postUser.sectors != null && postUser.sectors.length > 0;
    const haveClientsAlone = selectedClients.filter( selectedClient => { return !selectedSites.find( site => { return site.clientId === selectedClient.id; } ); } ).length > 0;
    const haveSitesAlone = selectedSites.filter( selectedSite => { return !selectedSectors.find( sector => { return sector.siteId === selectedSite.id; } ); } ).length > 0;

    setCanPost( emailChange && firstnameChange && lastnameChange && languageChange && timezoneChange && clientsChange && sitesChange && sectorsChange && !haveClientsAlone && !haveSitesAlone );
  };

  const checkHasChangeUser = () => {
    const clients = [];
    const sites = [];
    const sectors = [];
    if ( me ) {
      const connectedUserClients = CommonGlobalService.getUserClients( me );
      if ( connectedUserClients.length === 1 && !me.isSuperAdmin ) {
        clients.push( { id: connectedUserClients[ 0 ].id, isAdmin: false } );
      }
      const connectedUserSites = CommonGlobalService.getUserSites( me );
      if ( connectedUserSites.length === 1 && !me.isSuperAdmin ) {
        sites.push( { id: connectedUserSites[ 0 ].id, isAdmin: false } );
      }
      const connectedUserSectors = CommonGlobalService.getUserSectors( me );
      if ( connectedUserSectors.length === 1 && !me.isSuperAdmin ) {
        sectors.push( { id: connectedUserSectors[ 0 ].id, isAdmin: false } );
      }
    }

    const emailChange = postUser.email != null && postUser.email != defaultPostUser.email;
    const firstnameChange = postUser.firstname != null && postUser.firstname != defaultPostUser.firstname;
    const lastnameChange = postUser.lastname != null && postUser.lastname != defaultPostUser.lastname;
    const languageChange = postUser.language != null && postUser.language != defaultPostUser.language;
    const timezoneChange = postUser.timezone != null && postUser.timezone != defaultPostUser.timezone;
    const clientsChange = postUser.clients != null && postUser.clients.length > 0 && postUser.clients.sort().join() != clients.sort().join();
    const sitesChange = postUser.sites != null && postUser.sites.length > 0 && postUser.sites.sort().join() != sites.sort().join();
    const sectorsChange = postUser.sectors != null && postUser.sectors.length > 0 && postUser.sectors.sort().join() != sectors.sort().join();
    const abilitiesChange = postUser.abilities != null && postUser.abilities.length > 0;
    const skillsChange = postUser.skills != null && postUser.skills.length > 0;
    const readAccessSectorsChange = postUser.readAccessSectors != null && postUser.readAccessSectors != ( defaultPostUser.readAccessSectors ?? false );
    const appAccessChange = postUser.appAccess != null && postUser.appAccess != ( defaultPostUser.appAccess ?? 0 );
    setHasChange( emailChange || firstnameChange || lastnameChange || languageChange || timezoneChange || clientsChange || sitesChange || sectorsChange || abilitiesChange || skillsChange || readAccessSectorsChange || appAccessChange );
  };

  /*------------------------------ Events -------------------------------*/
  const onUserFieldChange = ( userField: ModifyUser ) => {
    postUser.email = userField.email != null ? userField.email : '';
    postUser.firstname = userField.firstname != null ? userField.firstname : '';
    postUser.lastname = userField.lastname != null ? userField.lastname : '';
    postUser.language = userField.language != null ? userField.language : '';
    postUser.timezone = userField.timezone != null ? userField.timezone : '';
    setPostUser( structuredClone( postUser ) );
  };

  const onClientsChange = ( clients: Client[] ) => {
    postUser.clients = clients.map( client => { return { id: client.id, isAdmin: ( client.isAdmin ?? false ) }; } );
    setPostUser( structuredClone( postUser ) );
    setSelectedClients( clients );
  };

  const onSitesChange = ( sites: Site[] ) => {
    postUser.sites = sites.map( site => { return { id: site.id, isAdmin: ( site.isAdmin ?? false ) }; } );
    setPostUser( structuredClone( postUser ) );
    setSelectedSites( sites );
  };

  const onSectorsChange = ( sectors: Sector[] ) => {
    postUser.sectors = sectors.map( sector => { return { id: sector.id, isAdmin: ( sector.isAdmin ?? false ) }; } );
    setPostUser( structuredClone( postUser ) );
    setSelectedSectors( sectors );
  };

  const onAbilitiesChange = ( abilities: Ability[] ) => {
    postUser.abilities = abilities.map( ability => { return { id: ability.id, expirationDate: ability.expirationDate ?? moment().add( 1, 'days' ).startOf( 'day' ).toISOString() }; } );
    setPostUser( structuredClone( postUser ) );
  };

  const onSkillsChange = ( skills: Skill[] ) => {
    postUser.skills = skills.map( skill => { return { id: skill.id, value: skill.value ?? 1 }; } );
    setPostUser( structuredClone( postUser ) );
  };

  const appNameToBit = ( app: APPS_NAME ): number => {
    switch ( app ) {
    case APPS_NAME.FLASHMEETING: return 0b1000;
    case APPS_NAME.KAMISHIBAI: return 0b0100;
    case APPS_NAME.RESOLUTION: return 0b0010;
    case APPS_NAME.PLANITEAM: return 0b0001;
    default: return 0;
    }
  };

  const onReadAccessSectorsChange = ( newValue: boolean ) => {
    postUser.readAccessSectors = newValue;
    setReadAccessSectors( newValue );
    setPostUser( structuredClone( postUser ) );
  };

  const onToggleAppAccess = ( app: APPS_NAME, checked: boolean ) => {
    const bit = appNameToBit( app );
    const newMask = checked ? ( appAccessMask | bit ) : ( appAccessMask & ~bit );
    setAppAccessMask( newMask );
    postUser.appAccess = newMask;
    setPostUser( structuredClone( postUser ) );
  };

  const onCreateClick = async () => {
    setIsLoading( true );
    postUser.readAccessSectors = readAccessSectors;
    postUser.appAccess = appAccessMask;
    const newUserId = await UserService.postUser( postUser, token, dispatch );
    if ( newUserId ) {
      toastService.showSuccess( t( '{0}\'s account has been created.' ).replace( '{0}', ( postUser.firstname + ' ' + postUser.lastname ) ) );
      resetForm();
    }
    setIsLoading( false );
  };


  /*------------------------------- Front -------------------------------*/
  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ hasChange } />
      <div className='relative flex flex-col min-w-[350px] h-full w-full'>
        <HomeTopBar title={ t( 'Create user' ) } needConfirmationToNav={ hasChange }/>
        <Breadcrumb
          needConfirmationToNav={ hasChange }
          links={
            [
              { label: t( 'common:user' ), route: ROUTES.USERS, icon: ( <UsersIcon className='h-[14px] select-none cursor-pointer'/> ) },
              { label: t( 'Create new user' ), route: ROUTES.NEW_USER, icon: ( <UserIcon className='h-[14px] select-none cursor-pointer'/> ) }
            ]
          }
        />
        <div className='flex flex-col items-center justify-center'>
          <form
            className='relative flex flex-col xl:flex-row gap-4 xl:gap-12 h-full w-full mb-12 items-center xl:items-start justify-center p-4'
            key={ rerenderLinks }
          >

            <AddUserFileds
              postUser={ postUser }
              onUserChange={ onUserFieldChange }
              defaultLanguage={ defaultLanguage }
              defaultTimezone={ defaultTimezone }
              t={ t }
            />

            <AddUserLinks
              postUser={ postUser }
              onClientsChange={ onClientsChange }
              onSitesChange={ onSitesChange }
              onSectorsChange={ onSectorsChange }
              onAbilitiesChange={ onAbilitiesChange }
              onSkillsChange={ onSkillsChange }
              t={ t }
            />

          </form>
          <div className='flex flex-col w-full max-w-[1000px] gap-6 px-4 pb-2'>
            <div className='flex flex-col w-full py-4 px-6 gap-4 sm:gap-2 border-y border-gray-base-light'>
              <div className='flex flex-col gap-2 sm:flex-row sm:gap-0 sm:items-center'>
                <span className='flex w-full font-bold flex-col'>
                  <span>{ t( 'translation:sectors_access' ) }</span>
                  <span className='text-[14px] font-normal text-gray-600'>
                    { t( 'translation:would_you_like_give_sectors_access' ) }
                  </span>
                </span>
                <div className='flex items-center'>
                  <Switch
                    className='sm:mx-6'
                    checked={ readAccessSectors }
                    onChange={ onReadAccessSectorsChange }
                  />
                </div>
              </div>
            </div>

            <div className='flex flex-col w-full py-4 px-6 gap-4 sm:gap-2 border-b border-gray-base-light'>
              <div className='flex flex-col gap-2'>
                <span className='flex w-full font-bold flex-col'>
                  <span>{ t( 'translation:application_access' ) }</span>
                  <span className='text-[14px] font-normal text-gray-600'>
                    { t( 'translation:give_or_remove_application_access' ) }
                  </span>
                </span>
                <div className='flex flex-row flex-wrap gap-6 pt-2'>
                  {
                    Object.values( APPS_NAME ).map( appName => {
                      const apps = getAppsFromMask( appAccessMask );
                      const checked = apps.includes( appName as APPS_NAME );
                      const info = ( APPS_INFOS as Record<string, { name: string; color?: string; icon?: JSX.Element }> )[ appName as string ];
                      const appLabel = info?.name ?? String( appName );
                      const iconEl = info?.icon as React.ReactElement<{ className?: string }> | undefined;
                      return (
                        <div key={ appName } className='flex items-center gap-4 border-[1px] border-gray-base-light rounded-md px-4 py-2 flex-shrink-0'>
                          <div className='flex items-center justify-center w-[48px] h-[48px] rounded-md text-white' style={ { background: info?.color ?? 'transparent' } }>
                            {
                              iconEl ? cloneElement( iconEl, { className: ( ( iconEl.props?.className ?? '' ) + ' fill-white' ).trim() } ) : null
                            }
                          </div>
                          <span className='text-sm'>{ t( `common:apps.${ appLabel.toLowerCase() }` ) }</span>
                          <Switch
                            checked={ checked }
                            onChange={ ( value: boolean ) => { onToggleAppAccess( appName as APPS_NAME, value ); } }
                          />
                        </div>
                      );
                    } )
                  }
                </div>
              </div>
            </div>
          </div>
          <div className='flex h-fit w-1/3 min-w-[160px] max-w-[250px] justify-center mt-10'>
            <Button
              label={ t( 'Create' ) }
              disable={ !canPost }
              icon={ <SaveIcon className='h-[23px] w-[23px] stroke-white'/> }
              color='#0bb173'
              colorHover='#088D5C'
              onClick={ onCreateClick }
            />
          </div>
        </div>
        {
          isLoading || !me ?
            <div className='absolute top-0 left-0 w-full h-full bg-gray-base-light/50 z-10 flex items-center justify-center'>
              <Loading/>
            </div>
            :
            null
        }
      </div>
    </>
  );

}